import React, { useEffect, useState } from "react";
import { hasRole, isAdminLevel } from "../../lib/roles";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { Users, ShoppingBag, BadgeIndianRupee, Hammer, Star, Clock, CalendarCheck, MessageCircle } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { useAuth } from "../../lib/auth";
import { assistGet, assistStart, assistEnd, assistSubscribe } from "../../lib/assist";
import { toast } from "sonner";
import OtpVerify from "../../components/OtpVerify";
import DateInput, { fmtDateIN } from "../../components/DateInput";

const COLORS = ["#6B1A2C", "#C9A063", "#A4823F", "#D4AF37", "#8E2438", "#501321"];
const BUCKETS = [["today", "Today"], ["week", "Week"], ["month", "Month"], ["six_months", "6 Months"], ["year", "Year"]];

/* ---------- shared bits ---------- */
const Kpi = ({ label, value, icon: Icon, to, sub }) => {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="eyebrow text-[9px]">{label}</div>
        {Icon && <Icon size={16} className="text-gold"/>}
      </div>
      <div className="font-display text-xl sm:text-3xl text-maroon mt-2 break-words leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-ash mt-1">{sub}</div>}
    </>
  );
  return to
    ? <Link to={to} className="bg-white border border-line p-5 block hover:border-maroon transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link>
    : <div className="bg-white border border-line p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</div>;
};

/** Year → today buckets rendered as a small stat strip */
const BucketStrip = ({ title, buckets, money = false }) => (
  <div className="bg-white border border-line p-4" data-testid={`buckets-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="eyebrow text-[10px] mb-3">{title}</div>
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
      {BUCKETS.map(([k, label]) => (
        <div key={k}>
          <div className="font-display text-lg text-maroon">{money ? fmtINR(buckets?.[k] || 0) : (buckets?.[k] ?? 0)}</div>
          <div className="text-[9px] text-ash uppercase tracking-widest">{label}</div>
        </div>
      ))}
    </div>
  </div>
);

const RatesStrip = () => {
  const [rates, setRates] = useState(null);
  useEffect(() => { api.get("/api/rates/current").then((r) => setRates(r.data)).catch(() => {}); }, []);
  if (!rates) return null;
  return (
    <div className="bg-maroon text-pearl p-4 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="daily-rates-strip">
      <span className="eyebrow text-gold-light text-[10px] w-full lg:w-auto">Today's Rates</span>
      <span>24K ₹{rates.gold_24k}/g</span>
      <span>22K ₹{rates.gold_22k}/g</span>
      <span>20K ₹{rates.gold_20k}/g</span>
      <span>18K ₹{rates.gold_18k}/g</span>
      <span>Silver ₹{rates.silver}/g</span>
    </div>
  );
};

/* ---------- Assist a Customer (login-for-customer) ---------- */
const EMPTY_NEW = {
  name: "", address: "", alt_phone: "", id_type: "Aadhaar", id_number: "",
  email: "", birthday: "", anniversary: "",
  referred_by_code: "", referred_by_phone: "",
};

/** Which detail fields we consider "complete". Used to nudge staff to fill the
 *  gaps in a returning customer's record. */
const MISSING_LABELS = {
  address: "Address",
  id_number: "Photo ID number",
  birthday: "Date of birth",
  anniversary: "Anniversary",
  alt_phone: "Other number",
  email: "Email",
};
const missingFields = (c) =>
  Object.keys(MISSING_LABELS).filter((k) => !(c?.[k] || "").toString().trim());

function AssistWidget() {
  const [assist, setAssist] = useState(assistGet());
  const [phone, setPhone] = useState("");
  const [checking, setChecking] = useState(false);
  // "unknown" until we've looked the number up; then "existing" or "new"
  const [stage, setStage] = useState("unknown");
  const [otpDone, setOtpDone] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW);
  const [refResult, setRefResult] = useState(null); // { found, name, phone } | { error }
  const [refBusy, setRefBusy] = useState(false);
  const resolveReferrer = async () => {
    const code = (form.referred_by_code || "").trim();
    const rphone = (form.referred_by_phone || "").trim();
    if (!code && !rphone) { toast.error("Enter a referral code or the referrer's phone"); return; }
    setRefBusy(true); setRefResult(null);
    try {
      const { data } = await api.get(`/api/crm/referral/resolve?code=${encodeURIComponent(code)}&phone=${encodeURIComponent(rphone)}`);
      setRefResult(data);
    } catch (e) {
      setRefResult({ error: e?.response?.data?.detail || "No customer found for that code or phone" });
    } finally { setRefBusy(false); }
  };
  // Existing-customer review + partial-data edit
  const [existing, setExisting] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_NEW);
  const [savingEdit, setSavingEdit] = useState(false);
  useEffect(() => assistSubscribe(() => setAssist(assistGet())), []);

  const reset = () => {
    setPhone(""); setStage("unknown"); setOtpDone(false);
    setForm(EMPTY_NEW); setExisting(null); setEditing(false);
  };

  // Step 1 — is this number already a customer?
  const lookup = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phone)) { toast.error("Enter the customer's valid 10-digit mobile number"); return; }
    setChecking(true);
    try {
      const { data } = await api.get(`/api/crm/customers/by-phone/${encodeURIComponent(phone)}`);
      if (data?.customer) {
        // Known customer — show their details first so staff can confirm and
        // fill any gaps before starting.
        setExisting(data.customer);
        setStage("existing");
        setEditing(false);
      } else {
        setStage("new");
        toast.info("New customer — please fill in their details below");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not look up that number");
    } finally { setChecking(false); }
  };

  const startExisting = () => {
    // First staff member to assist this customer becomes their "handled by".
    if (existing?.id) api.post(`/api/crm/customers/${existing.id}/handled`).catch(() => {});
    assistStart(phone, existing?.name || "");
    toast.success(`You are now assisting ${existing?.name || phone}`);
    reset();
  };

  const beginEdit = () => {
    setEditForm({
      name: existing?.name || "", address: existing?.address || existing?.location || "",
      alt_phone: existing?.alt_phone || "", id_type: existing?.id_type || "Aadhaar",
      id_number: existing?.id_number || "", email: existing?.email || "",
      birthday: existing?.birthday || "", anniversary: existing?.anniversary || "",
    });
    setEditing(true);
  };

  const saveExistingEdit = async (e) => {
    e.preventDefault();
    if ((editForm.id_type || "").toLowerCase() === "aadhaar" && (editForm.id_number || "").trim() && !/^\d{12}$/.test(editForm.id_number.replace(/\s/g, ""))) {
      toast.error("Aadhaar number must be exactly 12 digits"); return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/api/crm/customers/${existing.id}/enrich`, editForm);
      setExisting(data.customer || { ...existing, ...editForm });
      setEditing(false);
      toast.success("Customer details updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the details");
    } finally { setSavingEdit(false); }
  };

  // Step 2 — create the new customer, then start assisting
  const createAndStart = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Enter the customer's name"); return; }
    if (!form.address.trim()) { toast.error("Address is required for a new customer"); return; }
    if (!form.birthday) { toast.error("Date of birth is required for a new customer"); return; }
    if ((form.id_type || "").toLowerCase() === "aadhaar" && (form.id_number || "").trim() && !/^\d{12}$/.test(form.id_number.replace(/\s/g, ""))) {
      toast.error("Aadhaar number must be exactly 12 digits"); return;
    }
    if (!otpDone) { toast.error("Verify their mobile number with the OTP first"); return; }
    try {
      const { data } = await api.post("/api/crm/customers", { ...form, phone });
      assistStart(phone, data.name || form.name);
      toast.success(`You are now assisting ${data.name || form.name} — checkouts and karigar jobs will be linked to them`);
      reset();
    } catch (err) {
      if (err?.response?.status === 428) toast.error("Please verify the mobile number with the OTP first");
      else if (err?.response?.status === 409) {
        // Already a customer — bounce back to lookup so staff can assist them.
        toast.error(err?.response?.data?.detail || "This phone number already exists");
        setStage("unknown"); setOtpDone(false);
      }
      else toast.error(err?.response?.data?.detail || "Could not add the customer");
    }
  };

  if (assist) return <AssistProfile assist={assist}/>;

  return (
    <div className="bg-white border border-line p-4" data-testid="assist-widget">
      <div className="mb-3">
        <div className="eyebrow text-[10px]">Login for a Customer</div>
        <div className="text-[10px] text-ash">
          Walk-in? Enter their phone. If they're already with us you'll see their details;
          if they're new we'll take a few details first.
        </div>
      </div>

      {stage === "unknown" && (
        <form onSubmit={lookup} className="flex flex-wrap items-end gap-3">
          <input
            placeholder="Customer 10-digit phone" maxLength={10} inputMode="numeric"
            className="border border-line px-3 py-2 bg-pearl text-sm w-full sm:w-64"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            data-testid="assist-phone"
          />
          <button
            className="assist-pulse bg-maroon hover:bg-maroon-dark text-pearl text-xs font-semibold uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50 disabled:animate-none"
            disabled={checking}
            data-testid="assist-start"
          >
            {checking ? "Checking…" : "Start Assisting"}
          </button>
        </form>
      )}

      {/* ---- Existing customer: show details, let staff complete any gaps ---- */}
      {stage === "existing" && existing && (
        <div className="space-y-3" data-testid="assist-existing">
          <div className="flex items-center justify-between bg-gold/15 border border-gold px-3 py-2">
            <div className="text-xs text-maroon">
              <b>Existing customer</b> — {existing.name || phone} · {phone}
            </div>
            <button type="button" onClick={reset} className="text-[11px] text-maroon underline">
              Change number
            </button>
          </div>

          {!editing ? (
            <>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[12px]">
                <Detail label="Name" value={existing.name}/>
                <Detail label="Email" value={existing.email}/>
                <Detail label="Address" value={existing.address || existing.location}/>
                <Detail label="Other number" value={existing.alt_phone}/>
                <Detail label="Photo ID" value={[existing.id_type, existing.id_number].filter(Boolean).join(" · ")}/>
                <Detail label="Date of birth" value={existing.birthday ? fmtDateIN(existing.birthday) : ""}/>
                <Detail label="Anniversary" value={existing.anniversary ? fmtDateIN(existing.anniversary) : ""}/>
              </div>

              {missingFields(existing).length > 0 && (
                <div className="text-[11px] text-maroon bg-maroon/5 border border-maroon/20 px-3 py-2" data-testid="assist-missing">
                  Missing: {missingFields(existing).map((k) => MISSING_LABELS[k]).join(", ")}.
                  You can fill these in now.
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={startExisting}
                  className="assist-pulse bg-maroon hover:bg-maroon-dark text-pearl text-xs font-semibold uppercase tracking-wider px-4 py-2 transition-colors"
                  data-testid="assist-existing-start"
                >
                  Start Assisting
                </button>
                <button
                  onClick={beginEdit}
                  className="btn-outline text-xs"
                  data-testid="assist-existing-edit"
                >
                  Edit / complete details
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={saveExistingEdit} className="space-y-3" data-testid="assist-edit-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  placeholder="Full name" className="border border-line px-3 py-2 bg-pearl text-sm"
                  value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
                <input
                  placeholder="Email" className="border border-line px-3 py-2 bg-pearl text-sm"
                  value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value.toLowerCase() })}
                />
              </div>
              <textarea
                rows={2} placeholder="Address"
                className="w-full border border-line px-3 py-2 bg-pearl text-sm"
                value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
              <div className="grid sm:grid-cols-3 gap-3">
                <input
                  placeholder="Other number" maxLength={10} inputMode="numeric"
                  className="border border-line px-3 py-2 bg-pearl text-sm"
                  value={editForm.alt_phone}
                  onChange={(e) => setEditForm({ ...editForm, alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                />
                <select
                  className="border border-line px-3 py-2 bg-pearl text-sm"
                  value={editForm.id_type} onChange={(e) => setEditForm({ ...editForm, id_type: e.target.value })}
                >
                  {["Aadhaar", "PAN", "Driving Licence", "Voter ID", "Passport"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <input
                  placeholder="ID card number" className="border border-line px-3 py-2 bg-pearl text-sm"
                  value={editForm.id_number} onChange={(e) => setEditForm({ ...editForm, id_number: e.target.value })}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-sm"><span className="eyebrow text-[10px]">Date of birth</span>
                  <DateInput className="mt-1" value={editForm.birthday} onChange={(v) => setEditForm({ ...editForm, birthday: v })}/></label>
                <label className="block text-sm"><span className="eyebrow text-[10px]">Anniversary (optional)</span>
                  <DateInput className="mt-1" value={editForm.anniversary} onChange={(v) => setEditForm({ ...editForm, anniversary: v })}/></label>
              </div>
              <div className="flex gap-2">
                <button
                  className="bg-maroon hover:bg-maroon-dark text-pearl text-xs font-semibold uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50"
                  disabled={savingEdit} data-testid="assist-save-edit"
                >
                  {savingEdit ? "Saving…" : "Save details"}
                </button>
                <button type="button" onClick={() => setEditing(false)} className="btn-outline text-xs">Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ---- New customer form ---- */}
      {stage === "new" && (
        <form onSubmit={createAndStart} className="space-y-3">
          <div className="flex items-center justify-between bg-gold/15 border border-gold px-3 py-2">
            <div className="text-xs text-maroon">
              <b>New customer</b> — {phone}. Address, date of birth and OTP verification are required.
            </div>
            <button type="button" onClick={reset} className="text-[11px] text-maroon underline" data-testid="assist-cancel">
              Change number
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <input
              required placeholder="Full name" className="border border-line px-3 py-2 bg-pearl text-sm"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="assist-name"
            />
            <input
              placeholder="Email (optional)" className="border border-line px-3 py-2 bg-pearl text-sm"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
            />
          </div>

          <textarea
            required rows={2} placeholder="Address (required)"
            className="w-full border border-line px-3 py-2 bg-pearl text-sm"
            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="assist-address"
          />

          <div className="grid sm:grid-cols-3 gap-3">
            <input
              placeholder="Other number (optional)" maxLength={10} inputMode="numeric"
              className="border border-line px-3 py-2 bg-pearl text-sm"
              value={form.alt_phone}
              onChange={(e) => setForm({ ...form, alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
              data-testid="assist-alt-phone"
            />
            <select
              className="border border-line px-3 py-2 bg-pearl text-sm"
              value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })}
            >
              {["Aadhaar", "PAN", "Driving Licence", "Voter ID", "Passport"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <input
              placeholder="ID card number" className="border border-line px-3 py-2 bg-pearl text-sm"
              value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })}
            />
          </div>

          {/* Referral — who referred this customer (code or phone) */}
          <div className="border border-line rounded-lg p-3 bg-pearl/40">
            <div className="eyebrow text-[10px] mb-2">Referred by (optional)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                placeholder="Referral code" className="border border-line px-3 py-2 bg-white text-sm uppercase"
                value={form.referred_by_code} onChange={(e) => { setForm({ ...form, referred_by_code: e.target.value.toUpperCase() }); setRefResult(null); }}
                data-testid="assist-ref-code"
              />
              <input
                placeholder="Referrer's phone" className="border border-line px-3 py-2 bg-white text-sm" maxLength={10}
                value={form.referred_by_phone} onChange={(e) => { setForm({ ...form, referred_by_phone: e.target.value.replace(/\D/g, "").slice(0, 10) }); setRefResult(null); }}
                data-testid="assist-ref-phone"
              />
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button type="button" onClick={resolveReferrer} disabled={refBusy} className="btn-outline text-xs" data-testid="assist-ref-find">{refBusy ? "Finding…" : "Find referrer"}</button>
              {refResult?.found && <span className="text-xs text-green-700" data-testid="assist-ref-name">Referred by <b>{refResult.name}</b> ({refResult.phone})</span>}
              {refResult?.error && <span className="text-xs text-red-600" data-testid="assist-ref-error">{refResult.error}</span>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Date of birth (required)</span>
              <DateInput className="mt-1" value={form.birthday} onChange={(v) => setForm({ ...form, birthday: v })} data-testid="assist-birthday"/>
            </label>
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Anniversary (optional)</span>
              <DateInput className="mt-1" value={form.anniversary} onChange={(v) => setForm({ ...form, anniversary: v })} data-testid="assist-anniversary"/>
            </label>
          </div>

          <OtpVerify phone={phone} purpose="verify" onVerified={() => setOtpDone(true)} />

          <button
            className="bg-maroon hover:bg-maroon-dark text-pearl text-xs font-semibold uppercase tracking-wider px-4 py-2 transition-colors"
            data-testid="assist-create-start"
          >
            Add Customer &amp; Start Assisting
          </button>
        </form>
      )}
    </div>
  );
}

/** One label/value row in the existing-customer review — missing values are
 *  shown in maroon so gaps stand out. */
const Detail = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="text-ash">{label}:</span>
    {value ? <span className="text-ink">{value}</span> : <span className="text-maroon italic">not on file</span>}
  </div>
);

/* In-depth profile of the customer being assisted — know them before you sell */
function AssistProfile({ assist }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/api/crm/customers/by-phone/${encodeURIComponent(assist.phone)}`).then((r) => setD(r.data)).catch(() => {});
  }, [assist.phone]);
  const c = d?.customer;
  return (
    <div className="bg-gold/15 border-2 border-gold p-4 space-y-3" data-testid="assist-active">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="eyebrow text-[10px]">Assisting Customer</div>
          <div className="font-display text-xl text-maroon">{c?.name || assist.name || assist.phone} · {assist.phone}</div>
          <div className="text-[10px] text-ash">Browse the homepage/catalog to show them products — checkouts, personal catalogs and karigar jobs are linked to them.</div>
        </div>
        <div className="flex gap-2">
          <Link to="/" className="btn-gold text-xs" data-testid="assist-go-home">Show Homepage →</Link>
          <button onClick={() => { assistEnd(); toast.success("Assist mode ended"); }} className="btn-outline text-xs" data-testid="assist-end">End Assist</button>
        </div>
      </div>

      {c ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-center">
            {[
              ["LTV", fmtINR(c.ltv || 0)],
              ["Orders", c.orders_count || 0],
              ["Loyalty Pts", c.loyalty_points || 0],
              ["Schemes", (d.schemes || []).length],
              ["Karigar Jobs", (d.karigar_jobs || []).length],
            ].map(([l, v]) => (
              <div key={l} className="bg-white border border-line p-2">
                <div className="font-display text-lg text-maroon">{v}</div>
                <div className="text-[8px] uppercase tracking-widest text-ash">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-ash">
            {c.referral_code && <span>Referral: <b className="text-maroon">{c.referral_code}</b></span>}
            {c.birthday && <span>Birthday: {new Date(c.birthday).toLocaleDateString("en-GB")}</span>}
            {c.anniversary && <span>Anniversary: {new Date(c.anniversary).toLocaleDateString("en-GB")}</span>}
            {c.last_purchase_at && <span>Last purchase: {new Date(c.last_purchase_at).toLocaleDateString("en-GB")}</span>}
            {c.location && <span>Location: {c.location}</span>}
            {(c.tags || []).length > 0 && <span>Tags: {(c.tags || []).join(", ")}</span>}
          </div>
          {(d.orders || []).length > 0 && (
            <div className="text-[11px] text-ash">
              <b className="text-maroon">Recent buys:</b> {(d.orders || []).slice(0, 3).map((o) => (o.items || []).map((i) => i.name).join(", ")).filter(Boolean).join(" · ")}
              <span className="ml-2 italic">— use this to judge what to show them.</span>
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] text-ash">New customer — no purchase history yet. Start with bestsellers and the Smart Budget Match.</div>
      )}
    </div>
  );
}

/* ---------- SALESPERSON dashboard ---------- */
function SalespersonDash({ me, summary }) {
  const [reporting, setReporting] = useState(null);
  useEffect(() => { api.get("/api/crm/my-reporting").then((r) => setReporting(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <RatesStrip/>
      <AssistWidget/>
      {reporting && (reporting.reporting_to || []).length > 0 && (
        <div className="bg-white border border-line p-4" data-testid="sp-reporting-card">
          <div className="eyebrow text-[10px] mb-2">I Report To</div>
          <div className="flex flex-wrap gap-2">
            {(reporting.reporting_to || []).map((a) => (
              <span key={a.id} className="bg-gold/15 border border-gold/40 rounded-full px-3 py-1 text-xs">
                <b className="text-maroon">{a.name}</b> · {(a.role || "").replace("_", " ")}{a.phone ? ` · ${a.phone}` : ""}
              </span>
            ))}
            {reporting.report_wa_number && <span className="text-[10px] text-ash self-center">EOD reports go to WhatsApp {reporting.report_wa_number}</span>}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="My Customers" value={me.customers_added?.total ?? 0} icon={Users} to="/staff/customers" sub="Added by you"/>
        <Kpi label="Active Products" value={summary?.total_products ?? "—"} icon={ShoppingBag} to="/staff/products"/>
        <Kpi label="Karigar Jobs Given" value={me.karigar_jobs_given?.total ?? 0} icon={Hammer} to="/staff/karigar"/>
        <Kpi label="My Sales" value={me.my_sales_count?.total ?? 0} icon={BadgeIndianRupee} sub={`${me.grams_gold_sold_today}g gold · ${me.grams_silver_sold_today}g silver today`}/>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <BucketStrip title="My Attendance" buckets={me.attendance}/>
        <div className="bg-white border border-line p-4">
          <div className="eyebrow text-[10px] mb-3">Leaves & Averages</div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div><div className="font-display text-2xl text-maroon">{me.leave_days_taken_year ?? 0}</div><div className="text-[9px] text-ash uppercase tracking-widest">Leave days taken (approved)</div></div>
            <div><div className="font-display text-2xl text-maroon">{me.half_days_taken_year ?? 0}</div><div className="text-[9px] text-ash uppercase tracking-widest">Half days taken (approved)</div></div>
            <div><div className="font-display text-2xl text-maroon">{me.leaves_taken_year}</div><div className="text-[9px] text-ash uppercase tracking-widest">Days absent this year</div></div>
            <div><div className="font-display text-2xl text-maroon">{me.avg_customers_per_day_30d}</div><div className="text-[9px] text-ash uppercase tracking-widest">Avg customers/day (30d)</div></div>
          </div>
          <Link to="/staff/leave" className="btn-outline text-xs mt-3 inline-flex" data-testid="sp-apply-leave">Apply for Leave / Half Day</Link>
        </div>
        <BucketStrip title="Customers Added by Me" buckets={me.customers_added}/>
        <BucketStrip title="Schemes Started by Me" buckets={me.schemes_started}/>
        <BucketStrip title="My Sales Count" buckets={me.my_sales_count}/>
        <div className="bg-white border border-line p-4">
          <div className="eyebrow text-[10px] mb-3">CRM Features Used</div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div><div className="font-display text-2xl text-maroon">{me.crm_usage?.eod_reports?.total ?? 0}</div><div className="text-[9px] text-ash uppercase tracking-widest">EOD reports ({me.crm_usage?.eod_sent_to_wa ?? 0} sent to WA)</div></div>
            <div><div className="font-display text-2xl text-maroon">{me.crm_usage?.click_pack_sends?.total ?? 0}</div><div className="text-[9px] text-ash uppercase tracking-widest">Click-pack sends</div></div>
            <div><div className="font-display text-2xl text-maroon">{me.crm_usage?.wa_messages?.total ?? 0}</div><div className="text-[9px] text-ash uppercase tracking-widest">WhatsApp messages</div></div>
            <div><div className="font-display text-2xl text-maroon">{me.crm_usage?.eod_reports?.today ?? 0}</div><div className="text-[9px] text-ash uppercase tracking-widest">EOD today</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- KARIGAR dashboard ---------- */
function KarigarDash({ me, summary }) {
  const barData = [
    { name: "Gold jobs", value: me.gold_jobs?.total ?? 0 },
    { name: "Silver jobs", value: me.silver_jobs?.total ?? 0 },
  ];
  // Fix: clean the data (skip zero/empty entries, shorten long "Name (role)"
  // labels that used to overflow and blank the chart) before rendering.
  const pieData = Object.entries(me.by_assigner || {})
    .map(([name, value]) => ({ name: (name || "Unknown").replace(" (?)", "").slice(0, 24), value: Number(value) || 0 }))
    .filter((d) => d.value > 0);
  const pieTotal = pieData.reduce((a, d) => a + d.value, 0);
  return (
    <div className="space-y-6">
      <RatesStrip/>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Active Products" value={summary?.total_products ?? "—"} icon={ShoppingBag}/>
        <Kpi label="My Live Jobs" value={me.jobs_live ?? 0} icon={Hammer} to="/staff/karigar"/>
        <Kpi label="Avg Completion" value={me.avg_completion_hours != null ? `${me.avg_completion_hours}h` : "—"} icon={Clock} sub="Received → delivered"/>
        <Kpi label="My Earnings" value={fmtINR(me.earnings?.total ?? 0)} icon={BadgeIndianRupee} sub={`Outstanding ${fmtINR(me.outstanding ?? 0)}`}/>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-line p-4 text-center">
          <div className="eyebrow text-[10px] mb-2">Gold received by Swayambhu Gold (my jobs)</div>
          <div className="font-display text-3xl text-maroon">{me.gold_grams_received_total}g</div>
        </div>
        <div className="bg-white border border-line p-4 text-center">
          <div className="eyebrow text-[10px] mb-2">Silver received (my jobs)</div>
          <div className="font-display text-3xl text-maroon">{me.silver_grams_received_total}g</div>
        </div>
        <div className="bg-white border-2 border-gold p-4 text-center" data-testid="dash-gold-comp">
          <div className="eyebrow text-[10px] mb-2">Gold received AS PAYMENT (instead of money)</div>
          <div className="font-display text-3xl text-maroon">{me.gold_received_g ?? 0}g</div>
        </div>
        <div className="bg-white border-2 border-gold p-4 text-center" data-testid="dash-silver-comp">
          <div className="eyebrow text-[10px] mb-2">Silver received AS PAYMENT (instead of money)</div>
          <div className="font-display text-3xl text-maroon">{me.silver_received_g ?? 0}g</div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <BucketStrip title="Jobs Assigned to Me" buckets={me.jobs_assigned}/>
        <BucketStrip title="My Earnings (₹)" buckets={me.earnings} money/>
      </div>
      {/* Statistics: work received by whom + gold/silver bar + pie */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-line p-5">
          <div className="eyebrow mb-4">Gold vs Silver Jobs</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="name" tick={{fontSize: 11}}/><YAxis tick={{fontSize: 11}}/><Tooltip/>
              <Bar dataKey="value" fill="#C9A063"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-line p-5">
          <div className="eyebrow mb-4">Work Received — by Assigner</div>
          {pieData.length === 0 ? <div className="py-12 text-center text-ash text-sm">No jobs yet.</div> : (
            <>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} minAngle={4} isAnimationActive={false}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} job(s)`, n]}/>
                    <Legend wrapperStyle={{ fontSize: 10 }}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Always-visible breakdown under the chart */}
              <div className="mt-3 space-y-1">
                {pieData.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2 text-xs" data-testid={`assigner-${i}`}>
                    <span className="w-2.5 h-2.5 inline-block rounded-full" style={{ background: COLORS[i % COLORS.length] }}/>
                    <span className="flex-1">{d.name}</span>
                    <b className="text-maroon">{d.value}</b>
                    <span className="text-ash">({pieTotal ? Math.round((d.value / pieTotal) * 100) : 0}%)</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- ASTROLOGER dashboard ---------- */
function AstrologerDash({ me }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="My Rating" value={me.avg_rating != null ? `${me.avg_rating} ★` : "—"} icon={Star} sub={`${me.ratings_count} review(s)`}/>
        <Kpi label="Avg Reply Time" value={me.avg_response_minutes != null ? `${me.avg_response_minutes} min` : "—"} icon={Clock} sub={`${me.late_replies} late (>30 min)`}/>
        <Kpi label="Premium Chats" value={me.premium_chats ?? 0} icon={MessageCircle} sub={`${me.words_attended} words attended`}/>
        <Kpi label="My Earnings" value={fmtINR(me.revenue?.total ?? 0)} icon={BadgeIndianRupee} sub={`Outstanding ${fmtINR(me.outstanding ?? 0)} · comm. ${me.commission_pct}%`}/>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <BucketStrip title="My Attendance" buckets={me.attendance}/>
        <BucketStrip title="Chats Attended" buckets={me.chats}/>
        <BucketStrip title="My Revenue Share (₹)" buckets={me.revenue} money/>
        <div className="bg-white border border-line p-4">
          <div className="eyebrow text-[10px] mb-3">Payout Status</div>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-ash">Gross collected from customers</span><b>{fmtINR(me.gross_collected ?? 0)}</b></div>
            <div className="flex justify-between"><span className="text-ash">Your share ({100 - (me.commission_pct || 0)}%)</span><b>{fmtINR(me.revenue?.total ?? 0)}</b></div>
            <div className="flex justify-between"><span className="text-ash">Paid out by Swayambhu Gold</span><b>{fmtINR(me.total_paid_out ?? 0)}</b></div>
            <div className="flex justify-between border-t border-line pt-1"><span className="text-ash">To be paid (month-end)</span><b className="text-maroon">{fmtINR(me.outstanding ?? 0)}</b></div>
          </div>
          <Link to="/staff/astrology" className="btn-outline text-xs mt-3 inline-flex">Open Inbox & Payout Logs</Link>
        </div>
      </div>
    </div>
  );
}

/* ---------- ADMIN / MAJOR / SUPER dashboard ---------- */
function AdminDash({ data, stats, user }) {
  const [tab, setTab] = useState("project");
  const kpis = [
    { label: "Customers", value: data.total_customers, icon: Users, to: "/staff/customers" },
    { label: "Active Products", value: data.total_products, icon: ShoppingBag, to: "/staff/products" },
    { label: "Revenue (30d)", value: fmtINR(data.revenue_month), icon: BadgeIndianRupee, to: "/staff/revenue" },
    { label: "Karigar Jobs", value: data.active_karigar_jobs, icon: Hammer, to: "/staff/karigar" },
  ];
  const barData = [
    { period: "Today", value: data.orders_today * 1.0 },
    { period: "7d Revenue", value: data.revenue_week },
    { period: "30d Revenue", value: data.revenue_month },
    { period: "Total Revenue", value: data.revenue_total },
  ];
  return (
    <div className="space-y-6">
      <RatesStrip/>
      <AssistWidget/>
      {hasRole(user, ["super_admin", "major_admin"]) && (
        <div className="grid grid-cols-2 gap-4" data-testid="payout-shortcuts">
          <Link to="/staff/payouts" className="bg-maroon text-pearl p-4 flex items-center justify-between hover:bg-maroon/90">
            <div>
              <div className="eyebrow text-gold-light text-[9px]">Month-End Payment</div>
              <div className="font-display text-xl">Pay Astrologers →</div>
            </div>
            <BadgeIndianRupee size={22} className="text-gold-light"/>
          </Link>
          <Link to="/staff/payouts" className="bg-maroon text-pearl p-4 flex items-center justify-between hover:bg-maroon/90">
            <div>
              <div className="eyebrow text-gold-light text-[9px]">Month-End Payment</div>
              <div className="font-display text-xl">Pay Karigars →</div>
            </div>
            <BadgeIndianRupee size={22} className="text-gold-light"/>
          </Link>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => <Kpi key={k.label} {...k} sub="View details →"/>)}
      </div>

      <div className="flex border border-line bg-white w-full sm:w-fit overflow-x-auto">
        {[["project", "Project Statistics"], ["salespeople", `Salespeople (${stats?.salespeople?.length || 0})`], ["karigars", `Karigars (${stats?.karigars?.length || 0})`], ["astrologers", `Astrologers (${stats?.astrologers?.length || 0})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-xs whitespace-nowrap shrink-0 ${tab === k ? "bg-maroon text-pearl" : ""}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "project" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-line p-5">
            <div className="eyebrow mb-4">Sales by Category</div>
            {(data.by_category || []).length === 0 ? <div className="text-center py-12 text-ash text-sm">No sales recorded yet.</div> : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={data.by_category} dataKey="value" nameKey="name" outerRadius="75%" cx="50%" cy="50%">
                    {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                  </Pie>
                  <Tooltip/><Legend wrapperStyle={{fontSize: 11}}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-white border border-line p-5">
            <div className="eyebrow mb-4">Revenue Pulse</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="period" tick={{fontSize: 11}}/><YAxis tick={{fontSize: 11}}/><Tooltip formatter={(v) => fmtINR(v)}/>
                <Bar dataKey="value" fill="#6B1A2C"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === "salespeople" && (
        <div className="space-y-4">
          {(stats?.salespeople || []).map((sp) => (
            <details key={sp.user_id} className="bg-white border border-line" data-testid={`sp-stats-${sp.user_id}`}>
              <summary className="p-4 cursor-pointer flex flex-wrap items-center gap-x-6 gap-y-1">
                <b className="font-display text-lg text-maroon">{sp.name}</b>
                <span className="text-xs text-ash">Sales: {sp.my_sales_count?.total ?? 0} ({fmtINR(sp.my_sales_amount?.total ?? 0)})</span>
                <span className="text-xs text-ash">Customers: {sp.customers_added?.total ?? 0}</span>
                <span className="text-xs text-ash">Schemes: {sp.schemes_started?.total ?? 0}</span>
                <span className="text-xs text-ash">Karigar jobs: {sp.karigar_jobs_given?.total ?? 0}</span>
                <span className="text-xs text-ash">Today: {sp.grams_gold_sold_today}g Au · {sp.grams_silver_sold_today}g Ag</span>
                <span className="text-xs text-ash">Leaves: {sp.leave_days_taken_year ?? 0}d · Half days: {sp.half_days_taken_year ?? 0}</span>
                {(sp.reporting_to_names || []).length > 0 && <span className="text-[10px] text-gold-dark">Reports to: {(sp.reporting_to_names || []).join(", ")}{sp.reports_to_me ? " (you)" : ""}</span>}
              </summary>
              <div className="p-4 pt-0 grid lg:grid-cols-2 gap-3">
                <BucketStrip title="Sales Amount" buckets={sp.my_sales_amount} money/>
                <BucketStrip title="Customers Added" buckets={sp.customers_added}/>
                <BucketStrip title="Attendance" buckets={sp.attendance}/>
                <div className="bg-pearl border border-line p-3 text-xs">
                  <div className="eyebrow text-[9px] mb-2">Sales by Category</div>
                  {["Gold", "Silver"].map((metal) => (
                    <div key={metal} className="mb-1">
                      <b>{metal}:</b>{" "}
                      {Object.entries(sp.sales_by_category?.[metal] || {}).map(([c, n]) => `${c} ×${n}`).join(", ") || "—"}
                    </div>
                  ))}
                  <div className="mt-1 text-ash">EOD: {sp.crm_usage?.eod_reports?.total ?? 0} ({sp.crm_usage?.eod_sent_to_wa ?? 0} sent) · Click-pack: {sp.crm_usage?.click_pack_sends?.total ?? 0} · Days absent/yr: {sp.leaves_taken_year}</div>
                </div>
                <div className="bg-pearl border border-line p-3 text-xs lg:col-span-2">
                  <div className="eyebrow text-[9px] mb-2">Leave & Half-Day History (approved)</div>
                  {(sp.recent_leaves || []).length === 0 ? "No approved leaves yet." : (sp.recent_leaves || []).map((l) => (
                    <div key={l.id} className="border-b border-line/50 last:border-b-0 py-1.5">
                      <b className="text-maroon">{l.leave_type === "half_day" ? `Half day · ${l.half_day_date}` : `Leave · ${l.from_date} → ${l.to_date}`}</b> ({l.days}d)
                      <span className="text-ash"> — {l.reason} · handed over to {l.handover_to_name} · contact {l.contact_1} · back on {l.returning_on} · granted by {l.decided_by_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          ))}
          {(stats?.salespeople || []).length === 0 && <div className="text-ash text-sm p-6">No salespeople yet.</div>}
        </div>
      )}

      {tab === "karigars" && (
        <div className="space-y-4">
          {(stats?.karigars || []).map((k) => (
            <details key={k.user_id} className="bg-white border border-line" data-testid={`kg-stats-${k.user_id}`}>
              <summary className="p-4 cursor-pointer flex flex-wrap items-center gap-x-6 gap-y-1">
                <b className="font-display text-lg text-maroon">{k.name}</b>
                <span className="text-xs text-ash">Jobs: {k.jobs_assigned?.total ?? 0} · Live: {k.jobs_live} · Delivered: {k.jobs_delivered}</span>
                <span className="text-xs text-ash">Avg: {k.avg_completion_hours != null ? `${k.avg_completion_hours}h` : "—"}</span>
                <span className="text-xs text-ash">Earned: {fmtINR(k.earnings?.total ?? 0)} · Outstanding: {fmtINR(k.outstanding ?? 0)}</span>
              </summary>
              <div className="p-4 pt-0 grid lg:grid-cols-2 gap-3">
                <BucketStrip title="Jobs Assigned" buckets={k.jobs_assigned}/>
                <BucketStrip title="Earnings" buckets={k.earnings} money/>
                <div className="bg-pearl border border-line p-3 text-xs">
                  <div className="eyebrow text-[9px] mb-2">Metal Received</div>
                  Gold {k.gold_grams_received_total}g · Silver {k.silver_grams_received_total}g
                </div>
                <div className="bg-pearl border border-line p-3 text-xs">
                  <div className="eyebrow text-[9px] mb-2">Work Given By</div>
                  {Object.entries(k.by_assigner || {}).map(([n, c]) => `${n}: ${c}`).join(" · ") || "—"}
                </div>
              </div>
            </details>
          ))}
          {(stats?.karigars || []).length === 0 && <div className="text-ash text-sm p-6">No karigars yet.</div>}
        </div>
      )}

      {tab === "astrologers" && (
        <div className="space-y-4">
          {(stats?.astrologers || []).map((a) => (
            <details key={a.user_id} className="bg-white border border-line" data-testid={`ag-stats-${a.user_id}`}>
              <summary className="p-4 cursor-pointer flex flex-wrap items-center gap-x-6 gap-y-1">
                <b className="font-display text-lg text-maroon">{a.name}</b>
                <span className="text-xs text-ash">{a.avg_rating != null ? `${a.avg_rating} ★ (${a.ratings_count})` : "No ratings"}</span>
                <span className="text-xs text-ash">Chats: {a.chats?.total ?? 0} · Premium: {a.premium_chats}</span>
                <span className="text-xs text-ash">Reply: {a.avg_response_minutes != null ? `${a.avg_response_minutes} min` : "—"}</span>
                <span className="text-xs text-ash">Commission {a.commission_pct}% · Owed {fmtINR(a.outstanding ?? 0)}</span>
              </summary>
              <div className="p-4 pt-0 grid lg:grid-cols-2 gap-3">
                <BucketStrip title="Chats" buckets={a.chats}/>
                <BucketStrip title="Their Revenue Share" buckets={a.revenue} money/>
              </div>
            </details>
          ))}
          {(stats?.astrologers || []).length === 0 && <div className="text-ash text-sm p-6">No astrologers yet.</div>}
        </div>
      )}
    </div>
  );
}

/* ---------- entry ---------- */
export default function StaffDashboard() {
  const [summary, setSummary] = useState(null);
  const [roleStats, setRoleStats] = useState(null);
  const { user } = useAuth();
  const role = user?.role;
  // Personal figures per working role; older backends only send `me` for the primary role.
  const mine = roleStats?.mine || (roleStats?.me && role ? { [role]: roleStats.me } : {});

  useEffect(() => {
    // Live dashboard — pull the latest figures the moment the page opens and
    // then keep them fresh with a silent refresh every 5 seconds.
    const load = () => {
      api.get("/api/crm/dashboard/role-stats").then((r) => setRoleStats(r.data)).catch(() => {});
      api.get("/api/crm/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    };
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  if (!roleStats && !summary) return <div className="p-10">Loading…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="staff-dashboard">
      <div>
        <div className="eyebrow">Welcome back, {user?.name?.split(" ")[0]}</div>
        <h1 className="font-display text-4xl text-maroon">Dashboard</h1>
      </div>
      {/* One panel for every working role this person holds (e.g. salesperson + karigar). */}
      {mine.salesperson && <SalespersonDash me={mine.salesperson} summary={summary}/>}
      {mine.karigar && <KarigarDash me={mine.karigar} summary={summary}/>}
      {mine.astrologer && <AstrologerDash me={mine.astrologer}/>}
      {isAdminLevel(user) && summary && (
        <AdminDash data={summary} stats={roleStats} user={user}/>
      )}
      {Object.keys(mine).length === 0 && !isAdminLevel(user) && summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Active Products" value={summary.total_products} icon={ShoppingBag} to="/staff/products"/>
        </div>
      )}
    </div>
  );
}
